import { useEffect, useState, useCallback } from "react";
import { safeHtml } from "@/lib/html";
import client, { apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Spinner } from "@/components/bits";
import { Sparkles, RefreshCw, ShieldCheck, Clock, FileText, ChevronRight, Layers, ToggleRight, ToggleLeft, TrendingUp, CheckCheck, Copy, Check, Download, Code2, Rocket, Play, PauseCircle } from "lucide-react";

const labelColor = (l) => (l === "High" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : l === "Medium" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-slate-600 bg-slate-50 border-slate-200");

const SHORTCODES = [
  { code: "[ez_latest_update]", label: "All three blocks", hint: "Latest Update + Major Changes + FAQ (branded card)" },
  { code: '[ez_latest_update block="latest"]', label: "Latest Update only", hint: "Current status + official/expected updates + sources" },
  { code: '[ez_latest_update block="changes"]', label: "Major Changes only", hint: "“Major Changes This Year” section" },
  { code: '[ez_latest_update block="faq"]', label: "FAQ only", hint: "Questions + answers with JSON-LD schema" },
];

function ShortcodeRow({ sc }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(sc.code); setCopied(true); toast.success("Shortcode copied"); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Copy failed — select and copy manually"); }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="shortcode-row">
      <div className="min-w-0">
        <code className="block text-xs font-mono text-slate-800 truncate">{sc.code}</code>
        <span className="text-[11px] text-slate-500">{sc.label} — {sc.hint}</span>
      </div>
      <button data-testid="shortcode-copy-btn" onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:border-blue-500">
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateObj = (d) => d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
const nextDate = (r) => { if (!r.updated_at) return null; const d = new Date(r.updated_at); d.setDate(d.getDate() + (r.cadence_days || 7)); return d; };
const isNot2027 = (r) => !/2027/.test(r.post_title || "") && String(r.classification?.year || "") !== "2027";

function nextUtcMidnight() {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1, 0, 2));
}

function RolloutPanel({ rollout, onRun, onRetry, onSave, saving }) {
  const [limit, setLimit] = useState("");
  const [cadence, setCadence] = useState("");
  const dailyLimit = rollout?.daily_batch_limit, cadenceDays = rollout?.cadence_days;
  useEffect(() => { if (dailyLimit != null) setLimit(String(dailyLimit)); if (cadenceDays != null) setCadence(String(cadenceDays)); }, [dailyLimit, cadenceDays]);
  if (!rollout) return null;
  const pct = rollout.catalog_total ? Math.min(100, Math.round((rollout.managed / rollout.catalog_total) * 100)) : 0;
  const retryable = (rollout.failed_ids_count || 0) + (rollout.paused ? (rollout.progress?.pending || 0) : 0);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6" data-testid="rollout-panel">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-blue-700" />
          <h2 className="text-sm font-bold text-slate-700">Full-Site Rollout</h2>
          {rollout.running
            ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="rollout-running-badge"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Running</span>
            : rollout.paused
              ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5" data-testid="rollout-paused-badge"><PauseCircle className="h-3 w-3" /> Paused · AI budget</span>
              : <span className="text-[11px] text-slate-400">Idle · next auto-run 06:00 PM IST (12:30 UTC)</span>}
          {rollout.retarget_running && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5" data-testid="retarget-running-badge"><span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" /> Re-targeting → 2027</span>}
        </div>
        <div className="flex items-center gap-2">
          {retryable > 0 && !rollout.running && (
            <button data-testid="rollout-retry-failed-btn" onClick={onRetry} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:border-amber-500">
              <RefreshCw className="h-3.5 w-3.5" /> Retry failed ({retryable})
            </button>
          )}
          <button data-testid="rollout-run-now-btn" onClick={onRun} disabled={rollout.running} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-600 bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            <Play className="h-3.5 w-3.5" /> {rollout.paused ? "Resume now" : "Run today's batch now"}
          </button>
        </div>
      </div>
      {rollout.paused && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="rollout-paused-banner">
          <b>Paused — the AI key hit its daily spend limit</b> ({rollout.paused_at ? new Date(rollout.paused_at).toLocaleString() : ""}). {rollout.progress?.pending || 0} posts are kept in the queue; nothing was dropped.
          <div className="mt-1" data-testid="rollout-resume-eta"><b>Resumes automatically at {nextUtcMidnight().toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit" })} your time</b> (00:00 UTC, when the daily cap resets — probed every 30 min{rollout.last_budget_probe ? `; last probe ${new Date(rollout.last_budget_probe).toLocaleTimeString()}: ${rollout.last_budget_ok ? "OK" : "still exhausted"}` : ""}).
          To continue sooner, raise the cap: Profile → Universal Key → daily spend limit, then press "Resume now".</div>
        </div>
      )}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
          <span><b className="text-slate-700">{rollout.managed}</b> of {rollout.catalog_total} live posts generated · {rollout.published} published{rollout.drafts_managed > 0 && <span className="text-slate-400" title="Blocks generated for posts that are drafts/private in WordPress — they render only if the post is ever published"> · {rollout.drafts_managed} WP drafts (not counted)</span>}
            {rollout.running && rollout.progress?.total > 0 && (() => { const p = rollout.progress; const el = p.started ? (Date.now() - new Date(p.started).getTime()) / 60000 : 0; const rate = el > 0.5 ? p.done / el : 0; const eta = rate > 0 ? Math.round(p.pending / rate) : null; return (
              <span className="ml-2 text-emerald-700" data-testid="rollout-live-progress">· this run: <b>{p.done}</b>/{p.total} done{p.failed ? `, ${p.failed} failed` : ""}{rate > 0 && <> · {rate.toFixed(1)}/min{eta != null && <> · ~{eta >= 60 ? `${Math.floor(eta / 60)}h ${eta % 60}m` : `${eta}m`} left</>}</>}{rollout.concurrency ? ` · ${rollout.concurrency} parallel` : ""}</span>); })()}
            {!rollout.running && rollout.catch_up && rollout.managed < rollout.catalog_total && <span className="ml-2 text-amber-700" data-testid="rollout-catchup-note">· catch-up mode: next run covers ALL remaining posts, then {rollout.daily_batch_limit}/day refresh</span>}
          </span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} data-testid="rollout-progress-bar" />
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-[11px] text-slate-500">Posts per day
          <input data-testid="rollout-limit-input" type="number" min="1" max="300" value={limit} onChange={(e) => setLimit(e.target.value)} className="mt-1 block w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <label className="text-[11px] text-slate-500">Refresh every (days)
          <input data-testid="rollout-cadence-input" type="number" min="1" max="60" value={cadence} onChange={(e) => setCadence(e.target.value)} className="mt-1 block w-24 rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </label>
        <button data-testid="rollout-save-btn" onClick={() => onSave({ daily_batch_limit: Number(limit), cadence_days: Number(cadence) })} disabled={saving} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-blue-500 disabled:opacity-50">Save</button>
        {rollout.last_batch_at && (
          <span className="text-[11px] text-slate-400 ml-auto" data-testid="rollout-last-batch" title={rollout.missing_ids?.length ? `Deleted on WordPress (dropped from the catalog): ${rollout.missing_ids.join(", ")}` : undefined}>
            Last batch: {rollout.last_batch_count} of {rollout.last_batch_total || rollout.last_batch_count} refreshed
            {rollout.last_batch_failed ? <span className="text-amber-600"> · {rollout.last_batch_failed} failed{rollout.missing_ids?.length ? ` (${rollout.missing_ids.length} deleted on WP — skipped from now on)` : ""}</span> : null}
            {" · "}{new Date(rollout.last_batch_at).toLocaleString()}
          </span>
        )}
      </div>
      {!rollout.wp_shared && <p className="text-[11px] text-amber-600 mt-2">⚠ No shared WordPress session — reconnect WordPress so the scheduler can run.</p>}
    </div>
  );
}

export default function UpdateIntel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postId, setPostId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(null);
  const [batchQuery, setBatchQuery] = useState("");
  const [batch, setBatch] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [rollout, setRollout] = useState(null);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await client.get("/update-intel/updates"); setRows(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadRollout = useCallback(async () => {
    try { const { data } = await client.get("/update-intel/rollout/status"); setRollout(data); } catch { /* silent */ }
    try { const { data } = await client.get("/update-intel/priority-list?limit=12"); setPriority(data); } catch { /* silent */ }
  }, []);
  const [priority, setPriority] = useState(null);
  useEffect(() => { loadRollout(); const t = setInterval(() => { loadRollout(); load(); }, 10000); return () => clearInterval(t); }, [loadRollout, load]);

  const runRollout = async () => {
    try {
      const { data } = await client.post("/update-intel/rollout/run-now", {});
      toast.success(data.already_running ? "A batch is already running" : data.resumed ? `Resumed paused batch — ${data.resumed} posts left` : "Batch started — generating today's posts");
      loadRollout();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const retryFailedRollout = async () => {
    try {
      const { data } = await client.post("/update-intel/rollout/retry-failed", {});
      toast.success(data.already_running ? "A batch is already running" : data.queued ? `Re-queued ${data.queued} posts (${data.retried} previously failed)` : "Nothing to retry");
      loadRollout();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveRolloutCfg = async (patch) => {
    setSavingCfg(true);
    try { const { data } = await client.post("/update-intel/rollout/config", patch); toast.success("Rollout settings saved"); setRollout((r) => ({ ...r, ...data })); }
    catch (e) { toast.error(apiErr(e)); } finally { setSavingCfg(false); }
  };
  const retargetYear = async () => {
    try {
      const { data: prev } = await client.get("/update-intel/retarget-year/preview", { params: { year: "2027", scope: "exam" } });
      if (!prev.count) { toast.info("No flagged exam posts to re-target — all good."); return; }
      if (!window.confirm(`Re-target ${prev.count} flagged exam post(s) so their block leads with 2027?\n\nThis regenerates only the managed block — your WordPress post titles are NOT touched.`)) return;
      const { data } = await client.post("/update-intel/retarget-year", { year: "2027", scope: "exam" });
      toast.success(`Re-targeting ${data.queued} post(s) to 2027 — regenerating in the background.`);
      loadRollout();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const generate = async () => {
    const id = parseInt(postId, 10);
    if (!id) { toast.error("Enter a numeric WordPress post ID"); return; }
    setGenerating(true);
    try {
      const { data } = await client.post("/update-intel/generate", { post_id: id });
      setActive(data);
      toast.success(`Generated for “${data.post_title}” — ${data.has_official ? "official update" : "no official update yet"} (${data.confidence}%)`);
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setGenerating(false); }
  };

  const openDetail = async (pid) => {
    try { const { data } = await client.get(`/update-intel/updates/${pid}`); setActive(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const togglePublish = async () => {
    if (!active) return;
    setToggling(true);
    try {
      const next = !active.published;
      await client.post(`/update-intel/updates/${active.post_id}/publish-toggle`, { published: next });
      setActive((a) => ({ ...a, published: next }));
      setRows((rs) => rs.map((r) => (r.post_id === active.post_id ? { ...r, published: next } : r)));
      toast.success(next ? "Block published — plugin will render it" : "Block unpublished — hidden on the site");
    } catch (e) { toast.error(apiErr(e)); } finally { setToggling(false); }
  };

  const importTraffic = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter(Boolean);
      const rows = [];
      for (const line of lines) {
        const cells = line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
        const url = cells.find((c) => /^https?:\/\//i.test(c));
        if (!url) continue;
        const clicks = cells.map((c) => c.replace(/,/g, "")).find((c) => /^\d+$/.test(c)) || "0";
        rows.push({ url, clicks: Number(clicks) });
      }
      if (!rows.length) { toast.error("No URLs found in that CSV. Export Performance → Pages → CSV."); return; }
      const { data } = await client.post("/update-intel/traffic-import", rows);
      toast.success(`Traffic imported: ${data.matched} of ${rows.length} pages matched managed posts`);
      await load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const bulkPublish = async (published, onlyVerified) => {
    try {
      const { data } = await client.post("/update-intel/bulk-publish", { published, only_verified: onlyVerified });
      toast.success(`${published ? "Published" : "Unpublished"} ${data.updated} block${data.updated === 1 ? "" : "s"}`);
      await load();
      if (active) { try { const { data: d } = await client.get(`/update-intel/updates/${active.post_id}`); setActive(d); } catch (e) { console.error("update-intel refresh failed", e); } }
    } catch (e) { toast.error(apiErr(e)); }
  };

  const runBatch = async () => {
    const q = batchQuery.trim();
    if (!q) { toast.error("Enter a category or exam keyword"); return; }
    try {
      const { data } = await client.post("/update-intel/batch", { query: q });
      setBatch({ done: 0, total: data.total });
      const poll = async () => {
        try {
          const { data: job } = await client.get(`/jobs/${data.job_id}`);
          setBatch({ done: job.done, total: job.total });
          if (job.status === "done") { setBatch(null); toast.success(`Batch complete — ${job.done} posts processed`); await load(); return; }
          if (job.status === "error") { setBatch(null); toast.error(job.error || "Batch failed"); return; }
          setTimeout(poll, 3000);
        } catch (e) { setBatch(null); toast.error(apiErr(e)); }
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const now = new Date();
  const sortedRows = [...rows].sort((a, b) => {
    const na = nextDate(a), nb = nextDate(b);
    return (na ? na.getTime() : Infinity) - (nb ? nb.getTime() : Infinity);
  });

  return (
    <div className="max-w-7xl">
      <p className="text-xs font-mono uppercase tracking-widest text-blue-700">Automation</p>
      <h1 className="font-serif text-3xl font-bold text-slate-900 mt-1 mb-1">Update Intelligence</h1>
      <p className="text-slate-500 mb-6 max-w-2xl">Classify a post, research official sources, and generate a managed “Latest Update” block. The WordPress plugin inserts it after the first paragraph via the <code className="text-xs bg-slate-100 px-1 rounded">[ez_latest_update]</code> shortcode.</p>

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6" data-testid="shortcode-filter-panel">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-blue-700" />
            <h2 className="text-sm font-bold text-slate-700">Shortcode Block Filter</h2>
            <span className="text-[11px] text-slate-400">— copy any snippet and paste it where you want that block to appear</span>
          </div>
          <a data-testid="plugin-download-link" href={`${API}/download/connector`} className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:border-blue-500">
            <Download className="h-3.5 w-3.5" /> Download WordPress plugin
          </a>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {SHORTCODES.map((sc) => <ShortcodeRow key={sc.code} sc={sc} />)}
        </div>
        <p className="text-[11px] text-slate-400 mt-3">A single block renders “bare” (no card wrapper) so it blends into your article layout. Use no attribute for the full branded card. The <code className="bg-slate-100 px-1 rounded">[content_update_intelligence]</code> alias works identically.</p>
      </div>

      <RolloutPanel rollout={rollout} onRun={runRollout} onRetry={retryFailedRollout} onSave={saveRolloutCfg} saving={savingCfg} />
      {priority && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 mb-6" data-testid="priority-list-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2"><Clock className="h-4 w-4 text-amber-600" /> Priority list — refreshed first <span className="text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">{priority.total} posts</span></h2>
            <div className="text-[11px] text-slate-500">Events due this/next month, official-site changes this week{priority.changed_groups?.length ? ` (${priority.changed_groups.join(", ")})` : ""} and in-season boards refresh every 3 days instead of weekly.</div>
          </div>
          {priority.items.length > 0 && (
            <ul className="mt-3 grid md:grid-cols-2 gap-x-6 gap-y-1.5">
              {priority.items.map((p) => (
                <li key={p.post_id} className="flex items-start justify-between gap-2 text-xs" data-testid="priority-item">
                  <div className="min-w-0"><a href={p.link} target="_blank" rel="noreferrer" className="font-medium text-slate-800 hover:text-blue-700 line-clamp-1">{p.title}</a><div className="text-[11px] text-slate-500 line-clamp-1">{p.reasons.join(" · ")}{p.next ? ` · next: ${p.next}` : ""}</div></div>
                  <span className="shrink-0 text-[10px] font-mono text-amber-700">{p.urgency}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="update-intel-generate-bar">
        <div>
          <label className="block text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1">WordPress Post ID</label>
          <input data-testid="update-intel-postid-input" value={postId} onChange={(e) => setPostId(e.target.value)} placeholder="e.g. 583" className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 w-40" />
        </div>
        <button data-testid="update-intel-generate-btn" onClick={generate} disabled={generating} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          {generating ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {generating ? "Researching official sources…" : "Classify & Generate"}
        </button>
        <div className="h-8 w-px bg-slate-200 mx-1 hidden sm:block" />
        <div>
          <label className="block text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-1">Batch by category / exam</label>
          <input data-testid="update-intel-batch-input" value={batchQuery} onChange={(e) => setBatchQuery(e.target.value)} placeholder="e.g. CBSE, JEE Main…" className="rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 w-48" />
        </div>
        <button data-testid="update-intel-batch-btn" onClick={runBatch} disabled={!!batch} className="inline-flex items-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:border-indigo-500 disabled:opacity-60">
          {batch ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />} {batch ? `Generating ${batch.done}/${batch.total}…` : "Batch Generate"}
        </button>
        <div className="h-8 w-px bg-slate-200 mx-1 hidden sm:block" />
        <label data-testid="traffic-import-label" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-blue-500 cursor-pointer">
          <TrendingUp className="h-4 w-4" /> Import GSC CSV
          <input data-testid="traffic-import-input" type="file" accept=".csv" className="hidden" onChange={importTraffic} />
        </label>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-6">
        <div className="flex items-center justify-between gap-2 p-3 border-b border-slate-100 flex-wrap">
          <h2 className="text-sm font-bold text-slate-700">All Posts <span className="font-normal text-slate-400">({rows.length})</span> <span className="text-[11px] font-normal text-slate-400">— sorted by soonest next update · <span className="inline-block h-2 w-2 rounded-full bg-rose-500 align-middle" /> not 2027</span></h2>
          <div className="flex items-center gap-1.5">
            <button data-testid="retarget-year-btn" onClick={retargetYear} className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-rose-50 px-2.5 py-1 text-[11px] font-semibold text-rose-700 hover:border-rose-500"><span className="inline-block h-2 w-2 rounded-full bg-rose-500" /> Re-target flagged → 2027</button>
            <button data-testid="bulk-publish-verified-btn" onClick={() => bulkPublish(true, true)} className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:border-emerald-500"><CheckCheck className="h-3.5 w-3.5" /> Publish all verified</button>
            <button data-testid="bulk-publish-all-btn" onClick={() => bulkPublish(true, false)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-blue-500">Publish all</button>
            <button data-testid="bulk-unpublish-all-btn" onClick={() => bulkPublish(false, false)} className="rounded-lg border border-slate-300 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:border-rose-400 hover:text-rose-600">Unpublish all</button>
          </div>
        </div>
        {loading && <div className="p-6"><Spinner label="Loading…" /></div>}
        {!loading && rows.length === 0 && <div className="p-8 text-center text-slate-500">No managed updates yet. Generate one above or start the rollout.</div>}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="update-intel-table">
              <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left font-semibold px-3 py-2">Post</th>
                  <th className="text-left font-semibold px-3 py-2 hidden md:table-cell">Exam · Intent</th>
                  <th className="text-left font-semibold px-3 py-2">Status</th>
                  <th className="text-left font-semibold px-3 py-2"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Updated</span></th>
                  <th className="text-left font-semibold px-3 py-2"><span className="inline-flex items-center gap-1"><RefreshCw className="h-3 w-3" /> Next update</span></th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  const nd = nextDate(r);
                  const overdue = nd && nd < now;
                  return (
                    <tr key={r.post_id} data-testid="update-intel-row" onClick={() => openDetail(r.post_id)} className={`cursor-pointer border-t border-slate-100 ${active?.post_id === r.post_id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          {isNot2027(r) && <span data-testid="not-2027-dot" title="Title/heading is not targeting 2027 — check for an outdated year" className="inline-block h-2 w-2 shrink-0 rounded-full bg-rose-500" />}
                          <span className="font-semibold text-slate-800 truncate max-w-[300px]">{r.post_title}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 font-mono">#{r.post_id} · v{r.version} · {r.published ? <span className="text-emerald-600">published</span> : <span className="text-slate-400">unpublished</span>}</div>
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell text-xs text-slate-500">{r.classification?.exam || "—"}<div className="text-slate-400">{r.classification?.primary_intent}</div></td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${labelColor(r.confidence_label)}`}>{r.confidence}%</span>
                        {r.has_official && <span className="ml-1 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">Official</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600 whitespace-nowrap" data-testid="row-updated-date">{fmtDate(r.updated_at)}</td>
                      <td className="px-3 py-2 text-xs whitespace-nowrap" data-testid="row-next-date">
                        {nd ? <span className={overdue ? "font-semibold text-rose-600" : "text-slate-600"}>{overdue ? "⚠ due " : ""}{fmtDateObj(nd)}</span> : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {active && (
        <div className="space-y-4" data-testid="update-intel-detail">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h2 className="font-serif text-lg font-bold text-slate-900">{active.post_title}</h2>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${labelColor(active.confidence_label)}`}><ShieldCheck className="h-3.5 w-3.5" /> {active.confidence_label} · {active.confidence}%</span>
                <button data-testid="update-intel-publish-toggle" onClick={togglePublish} disabled={toggling} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold disabled:opacity-50 ${active.published ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500"}`}>
                  {active.published ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />} {active.published ? "Published" : "Unpublished"}
                </button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-500">
              <span>Exam: <b>{active.classification?.exam || "—"}</b></span>
              <span>· Intent: <b>{active.classification?.primary_intent}</b></span>
              <span>· Updated: <b>{fmtDate(active.updated_at)}</b></span>
              <span>· Next update: <b>{active.updated_at ? fmtDateObj(nextDate(active)) : "—"}</b> (every {active.cadence_days}d)</span>
              <span>· Auto-publish: <b className={active.auto_publish ? "text-emerald-600" : "text-slate-400"}>{active.auto_publish ? "yes" : "gated"}</b></span>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Rendered block preview (what the plugin inserts)</p>
            <div data-testid="update-intel-block-preview" dangerouslySetInnerHTML={{ __html: safeHtml(active.block_html) }} />
          </div>
        </div>
      )}
    </div>
  );
}
